// Product words: German UI strings the code owns (status, pipeline and toast words, descriptor
// keys, the row-pill words). Words a tenant serves come from the vocabulary, not from here.
import type { DraftKind } from './serverTypes';
import type { FaceFn } from './services/categoryMeta';

// ─── OCR Options ─────────────────────────────────────
export const OCR_OPTIONS = [
  { value: 'auto', label: 'KI' },
  { value: 'force', label: 'OCR' },
  { value: 'skip', label: 'Skip' },
] as const;

// The settled statuses (the Entschieden tab) live in services/settledRow.ts (SETTLED_STATUSES) —
// one home, read by every gate that asks whether a row is decided.

// ─── AI descriptors ─────────────────────────────────────────────────────────────────────────
// The KEYS of the AI descriptor split pills — pipeline nouns, the same word on every surface and
// tenant (detail, Statistik, export, PDF; core carries the same strings). They name the pipeline
// stage, not the role's artefact, so no role can conflate them: a Kategorie, a Marktprozess and a
// Bearbeitung all have a Klassifikationskonfidenz. The older role-flavoured names (Kategoriekonfidenz,
// Antwortsicherheit) are retired on every tenant. Neither is an empirical rate: the approval rate is
// the Abnahmequote, a separate metric.
export const AI_DESCRIPTOR = {
  /** The classifier's self-reported confidence in its primary-axis answer. */
  classification: 'Klassifikationskonfidenz',
  /** The composer's self-reported confidence in the letter it wrote. */
  suggestion: 'Vorschlagskonfidenz',
  /** created_at → the suggestion's completion, as the row's processing_time_ms. */
  responseTime: 'KI-Antwortzeit',
  /** The key of a row no composer wrote a letter for; its value is NO_DRAFT_FACE. */
  noDraft: 'Kein Entwurf',
} as const;

/** The value segment of the no-draft descriptor: the clerk answers by hand. */
export const NO_DRAFT_FACE = 'Von Hand';

// ─── Kopieren confirmations (Part C, 2026-09-07) ─────────────────
// A copy records an indirect approval, so it is stated and it is takeable-back. Both hosts that can
// show the toast — AntwortenSeite and the pop-out window — say the SAME thing for the same act and
// leave the SAME window to undo it; the wording and the duration therefore live here once rather
// than as four literals two files apart.
export const COPY_TOAST_MESSAGE = {
  /** The letter flow: one suggestion, copied whole. */
  suggestion: 'Vorschlag kopiert — zählt als genehmigt',
  /** One triage draft of a row that may carry two. */
  draft: 'Entwurf kopiert — zählt als genehmigt',
} as const;

// The RECEIPT's own toast (R31/R32, 2026-09-18) is built in copyToastMessage below from the SERVED
// `draft_kind.ack` word (part 4c, 2026-09-27) — the code-owned `receipt` variant this object used
// to carry is deleted outright (no-deprecation-ceremonies). It is still the one copy toast that
// does NOT say „zählt als genehmigt", because it does not: an Empfangsbestätigung tells the sender
// their mail arrived; it settles nothing about how the row will be handled, records no
// copy-approval server-side, and counts toward no approval rate.

/**
 * THE copy toast for a gesture, so the two hosts that show one (AntwortenSeite and PopoutShell)
 * cannot pick differently. `kind` is the triage draft that was copied, or undefined for the letter
 * flow's single suggestion. `face` is the tenant's served vocabulary (`useCategoryMeta().face`) —
 * the receipt toast names the SERVED word, so a tenant that re-words `draft_kind.ack` sees its own
 * word in the toast without a line here changing.
 */
export function copyToastMessage(kind: DraftKind | null | undefined, face: FaceFn): string {
  if (!kind) return COPY_TOAST_MESSAGE.suggestion;
  return kind === 'ack' ? `${face('draft_kind', 'ack') ?? 'ack'} kopiert` : COPY_TOAST_MESSAGE.draft;
}

// ─── Personal Data Field Labels ─────────────────────

type ApiFieldKey =
  | 'emails'
  | 'phoneNumbers'
  | 'ibans'
  | 'dates'
  | 'postalCodes'
  | 'names';

// Keys match API JSON field casing (camelCase).
export const PERSONAL_DATA_LABELS: Record<ApiFieldKey, string> = {
  emails: 'E-Mail-Adressen',
  phoneNumbers: 'Telefonnummern',
  ibans: 'IBANs',
  dates: 'Datumsangaben',
  postalCodes: 'Postleitzahlen',
  names: 'Namen',
};

export const normalizeKey = (key: string): ApiFieldKey =>
  (key[0].toLowerCase() + key.slice(1)) as ApiFieldKey;

// ─── Status Labels (German UI) ──────────────────────
// 'approved' deliberately has NO entry here (fix wave 2026-09-27, item 2): an approved ticket's
// status word IS the served `decision.approve` face („Genehmigt" on the seed) — the same word the
// Statistik card and the export wear for the approved count — not a second, code-owned literal a
// re-worded tenant would leave stale. `statusLabel` below is the one place that seam is composed;
// every other status here names no served id and stays a plain floor.
export const STATUS_LABELS: Record<string, string> = {
  // The „Nicht hilfreich" verdict (core 2026-10-16): an ACTIVE status since "Genehmigen settles the
  // row" (2026-10-23) — a declined row sits in Entschieden beside the approved ones until filed.
  declined: 'Nicht hilfreich',
  submitted: 'Eingereicht',
  ingested: 'Aufgenommen',
  categorizing: 'Kategorisierung',
  categorized: 'Kategorisiert',
  generating_suggestion: 'Vorschlag wird erstellt',
  suggestion_ready: 'Vorschlag bereit',
  sent: 'Gesendet',
  // Names no served id — no decision/bucket/outcome/draft_kind family serves a "reject" word, and
  // this status predates the current triage model (ruled chrome, fix wave 2026-09-27, item 2; see
  // badgeFactLiterals.test.ts's CHROME_EXCEPTIONS for the same ruling).
  rejected: 'Abgelehnt',
  reanalyzing: 'Erneut analysiert',
  pipeline_error: 'Fehler',
  archived: 'Archiviert',
} as const;

/** A ticket's pipeline status, in German. `approved` is the one status that IS a served id (see
 *  the comment on STATUS_LABELS above) — read through the `decision.approve` face the Statistik card
 *  and the approved-as badge already wear, so a re-worded decision word reaches this label too.
 *  (Since "Genehmigen settles the row" the TAB is Entschieden and holds the no as well, so the tab's
 *  word is no longer the approved status's word.) Every other status floors to STATUS_LABELS, then
 *  to the raw key — the same floor this table has always had, status by status.
 *  `configLoading` is a SEPARATE signal from `face`'s own return (fix loop pass 1, item 3):
 *  resolveFace's raw-key floor answers the literal key for an unresolved vocabulary (its
 *  absent-family branch, not its served-null one), so `face(...) ?? status` alone painted a raw key
 *  on the clerk's Status field for the whole loading window. While loading this floors to an empty
 *  value instead (both call sites render a plain string, not JSX, so a SkeletonBlock cannot stand
 *  in here). Once config settles, an explicit served `null` keeps the raw-status-key fallback. */
export function statusLabel(status: string | null | undefined, face: FaceFn, configLoading: boolean): string {
  if (!status) return '';
  if (status === 'approved') return configLoading ? '' : (face('decision', 'approve') ?? status);
  return STATUS_LABELS[status] ?? status;
}

export const PIPELINE_STAGE_LABELS: Record<string, string> = {
  parse: 'Parsen',
  pii_extraction: 'Personendaten-Erkennung',
  attachment_extraction: 'Anhang-Extraktion',
  categorization: 'Kategorisierung',
  ocr_execution: 'OCR',
  suggestion_generation: 'Antwortgenerierung',
  draft_generation: 'Entwurfserstellung',
};

// THE „Formular" word (part 7 fix loop, 2026-10-12) — ONE constant for the row pill's face
// (FormPill), its „Hinweise in der Liste" switch (services/rowPillKinds) and its filter bubble
// (FilterPanel), so the three can never word the one fact two ways. A PRODUCT word, not a served
// tenant word: form extraction is a capability (/config `formExtractionEnabled`), not a vocabulary
// value, so no tenant serves a face for it.
export const FORM_WORD = 'Formular';

// THE „Frist genannt" word — one constant for the list row's pill (FristGenanntPill) and its
// „Hinweise in der Liste" switch (services/rowPillKinds). A product word on every role: the
// letter check finds a deadline in the mail, no tenant vocabulary serves it.
export const FRIST_GENANNT_WORD = 'Frist genannt';

// Escalation-tier chip (eskalationsstufe 1–3) — the call sites (TicketDetail, DashboardSeite,
// KlaerfallEditor, this file's own TicketRowMeta below, and a since-retired no-draft card) each
// drew their own copy of this span before the chip register sweep (Part C wave 2, 2026-09-09),
// which is exactly the kind of drift the pill house rule exists to prevent (see
// SecondaryAxisPills.tsx).
//
// Part 7, 2026-10-11 (operator ruling): the chip is an INFO pill — outlined, white fill, short,
// icon-free — so it sits in the info tier beside the other outlined pills. It renders for N >= 1
// only: Stufe 0 is the absence of an escalation, never a chip. The metrics come from
// `pillStyle('info', variant)` (a caller on a dense row wants 'row', a caller that is the WHOLE
// header content wants 'detail'); the face does not route through `pillFace` — it is a fixed
// signal, not free prose.
//
// Small facts on a row (operator 2026-10-19, round 3 B, adopted whole): the face is the ROMAN
// NUMERAL of the step (I, II, III) in ORDINARY info-pill ink — the three plum inks the chip wore
// clashed with the sender pill, and plum is the sender pill's alone again. The numeral hovers the
// prompt's own words for the step (delayed, like every hover: a numeral is an abbreviation). The
// kind ships HIDDEN by default (services/rowPillKinds DEFAULT_HIDDEN_ROW_PILLS); a clerk or a
// tenant switches it on under „Hinweise in der Liste".
export const STUFE_FACE: Readonly<Record<1 | 2 | 3, string>> = { 1: 'I', 2: 'II', 3: 'III' };

export const STUFE_HOVER: Readonly<Record<1 | 2 | 3, string>> = {
  1: '1. Erinnerung',
  2: '2. Erinnerung',
  3: 'Letzte Erinnerung, dringend',
};
